import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { PARTNERSHIP_OFFERS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUDGETS = ["< $10k", "$10k – $25k", "$25k – $50k", "$50k – $100k", "$100k+"];
const PLATFORMS = ["Instagram", "TikTok", "YouTube"];
const OFFER_COLORS = ["bg-zinc-300 text-obsidian", "bg-yellow text-obsidian", "bg-coral text-white", "bg-white text-obsidian"];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", brand: "", budget: "", platform: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.brand || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/partnership-inquiry`, form);
      toast.success("Sent! Justin's team will be in touch soon. 🎉");
      setForm({ name: "", email: "", brand: "", budget: "", platform: "", message: "" });
    } catch (err) {
      toast.error("Something broke. Email rob@leusner.co directly!");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-white/5 border-2 border-white/15 focus:border-blue outline-none px-5 py-4 rounded-2xl font-outfit font-medium text-white placeholder:text-white/40 transition-colors duration-200";

  return (
    <section id="contact" data-testid="contact" className="py-20 md:py-32 px-5 md:px-10">
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-14 lg:gap-20">
        <div>
          <span className="font-outfit text-xs uppercase tracking-widest text-yellow">Partnerships</span>
          <h2 className="font-anton uppercase text-5xl md:text-7xl leading-[0.9] text-yellow mt-4">
            Let&apos;s cook <br /> something <span className="text-coral">up.</span>
          </h2>
          <p className="font-outfit font-medium text-yellow mt-6 max-w-md text-base md:text-lg leading-relaxed">
            Got a brand, a budget, and a wild idea? Drop it below, it goes straight to Justin&apos;s partnerships team.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {PARTNERSHIP_OFFERS.map((o, i) => (
              <motion.div
                key={o.title}
                whileHover={{ y: -5, rotate: i % 2 ? 2 : -2 }}
                className={`rounded-2xl p-6 ${OFFER_COLORS[i]}`}
              >
                <h3 className="font-anton uppercase text-lg">{o.title}</h3>
                <p className="font-outfit font-medium text-sm mt-2 leading-relaxed opacity-80">{o.body}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          data-testid="partnership-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col gap-5 bg-obsidian rounded-3xl p-6 md:p-10 border-2 border-white/10"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <input data-testid="form-name" className={inputCls} placeholder="Your Name *" value={form.name} onChange={update("name")} />
            <input data-testid="form-brand" className={inputCls} placeholder="Brand / Company *" value={form.brand} onChange={update("brand")} />
          </div>
          <input data-testid="form-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={update("email")} />

          <div>
            <label className="font-outfit text-xs uppercase tracking-widest text-white/50">Budget Range</label>
            <div className="flex flex-wrap gap-2 mt-3">
              {BUDGETS.map((b) => (
                <button
                  key={b}
                  type="button"
                  data-testid={`budget-${b}`}
                  onClick={() => setForm((f) => ({ ...f, budget: b }))}
                  className={`font-outfit font-bold text-xs uppercase tracking-wide px-4 py-2 rounded-full border-2 transition-colors duration-200 ${
                    form.budget === b
                      ? "bg-blue text-obsidian border-blue"
                      : "border-white/20 text-white/60 hover:border-white/50"
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="font-outfit text-xs uppercase tracking-widest text-white/50">What platform are you interested in?</label>
            <div className="flex flex-wrap gap-2 mt-3">
              {PLATFORMS.map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`platform-${p}`}
                  onClick={() => setForm((f) => ({ ...f, platform: p }))}
                  className={`font-outfit font-bold text-xs uppercase tracking-wide px-4 py-2 rounded-full border-2 transition-colors duration-200 ${
                    form.platform === p
                      ? "bg-coral text-white border-coral"
                      : "border-white/20 text-white/60 hover:border-white/50"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <textarea data-testid="form-message" rows={4} className={`${inputCls} resize-none`} placeholder="Tell me about the project *" value={form.message} onChange={update("message")} />

          <motion.button
            type="submit"
            data-testid="form-submit"
            disabled={submitting}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="font-outfit font-extrabold text-sm uppercase tracking-wide bg-yellow text-obsidian px-8 py-4 rounded-full hover:bg-blue transition-colors duration-200 disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Send It 🚀"}
          </motion.button>
        </motion.form>
      </div>
    </section>
  );
};
